import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import './Industries.css';

const industries = [
  {
    industry: 'Automotive',
    slug: 'automotive',
    title: 'Plant knowledge that never leaves the plant',
    desc: 'Sequencing, quality narratives, supplier docs, and line-side knowledge without shipping plant data offshore.',
    features: [
      'Private corpus over SOPs and quality records',
      'Line-side and supplier document assistants',
      'Sequence and exception narratives on-prem',
      'Deploy MyAlpha Rack or MyAlpha on plant NVIDIA hardware',
    ],
  },
  {
    industry: 'Heavy Equipment',
    slug: 'heavy-equipment',
    title: 'Service intelligence at the dealer and the yard',
    desc: 'Service manuals, VMI language, field diagnostics, and parts intelligence on-prem at dealers and plants.',
    features: [
      'Manual and parts retrieval over private indexes',
      'Field diagnostic assistants without cloud upload',
      'VMI and replenishment language models',
      'Dealer and plant network residency',
    ],
  },
  {
    industry: 'Medical',
    slug: 'medical',
    title: 'Clinical and ops assistants inside your boundary',
    desc: 'Clinical and operational assistants that respect PHI boundaries; your retention and access rules, not a consumer AI’s.',
    features: [
      'PHI-aware deployment and access controls',
      'Retention and audit you define',
      'Ops and clinical workflow assistants',
      'Air-gap capable for sensitive facilities',
    ],
  },
  {
    industry: 'Supply Chain',
    slug: 'supply-chain',
    title: 'Freight and exception playbooks competitors never see',
    desc: 'Freight, routing exceptions, contract clauses, and disruption playbooks with data that competitors never see.',
    features: [
      'Contract and rate clause assistants',
      'Exception and disruption playbooks',
      'Private freight and route corpora',
      'On-prem MyAlpha for logistics HQs and hubs',
    ],
  },
  {
    industry: 'Legal',
    slug: 'legal',
    title: 'Client work that stays in the firm',
    desc: 'Contract review, discovery support, and research assistants that don’t upload client files to a shared cloud model.',
    features: [
      'Matter-scoped retrieval and access',
      'Contract and discovery assistants',
      'No multi-tenant model shared with outsiders',
      'Policy and retention set by the firm',
    ],
  },
  {
    industry: 'Manufacturing',
    slug: 'manufacturing',
    title: 'Tribal knowledge for the floor',
    desc: 'Work instructions, CAPA narratives, and tribal knowledge captured for the floor — offline-capable when the plant requires it.',
    features: [
      'Work instruction and CAPA assistants',
      'Offline-capable plant deployments',
      'Private tribal knowledge capture',
      'MyAlpha Rack sized for plant concurrency',
    ],
  },
  {
    industry: 'Energy & Utilities',
    slug: 'energy-and-utilities',
    title: 'Procedures inside OT and IT boundaries',
    desc: 'Procedures, outage response, and regulatory language inside OT/IT boundaries.',
    features: [
      'Procedure and outage response assistants',
      'Regulatory language on controlled hardware',
      'Segregated OT/IT-friendly deployment',
      'Air-gappable MyAlpha for critical sites',
    ],
  },
  {
    industry: 'Financial Services',
    slug: 'financial-services',
    title: 'Internal research you can defend to auditors',
    desc: 'Internal research and ops assistants with residency and retention you can defend to auditors.',
    features: [
      'Residency and retention you control',
      'Internal research and ops assistants',
      'Identity and access tied to your stack',
      'Audit-friendly logging on your terms',
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1] },
  },
};

export const Industries = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace('#', '');
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  return (
    <motion.div
      className="industries-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <section className="industries-hero">
        <div className="container">
          <motion.div className="industries-hero-content" variants={itemVariants}>
            <h1>
              Industry LLMs that speak your language — and stay in your building.
            </h1>
            <p className="lead">
              Every MyAlpha deployment starts with how your sector works: documents,
              regulations, slang, and failure modes. Below are the packs we build and
              extend.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="industries-main">
        <div className="container">
          <motion.div
            className="industries-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            {industries.map((item) => (
              <motion.article
                key={item.industry}
                id={item.slug}
                className="industry-card"
                variants={itemVariants}
              >
                <span className="industry-tag">{item.industry}</span>
                <h2>{item.title}</h2>
                <p className="industry-desc">{item.desc}</p>
                <ul className="industry-features">
                  {item.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <Link
                  to={`/contact?source=industry&pack=${encodeURIComponent(item.industry)}`}
                  className="industry-cta"
                >
                  Discuss this pack
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="custom-pack-section">
        <div className="container">
          <motion.div
            className="custom-pack"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Don’t see your vertical?</h2>
            <p>
              We build custom industry packs — same MyAlpha hardware, your vocabulary.
            </p>
            <Link to="/contact?source=industry-custom" className="btn btn-primary">
              Talk about a custom pack
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Industries;
